/**
 * Essay Editor
 * Two-tab editor: Content (Page blocks) and Questions (essay question settings)
 */

import * as React from 'react';
import {
    Input,
    Label,
    Checkbox,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    cn,
    RichTextEditor,
    createEmptyDocument,
    isTiptapDocument,
    createParagraphContent,
} from '@ai-content-hub/content-kit';
import type { TiptapJSON } from '@ai-content-hub/content-kit';
import {
    type EditTabDescriptor,
    CompositeEditShell,
    CompositeEditContentTab,
    useLocale,
} from '@ai-content-hub/content-core';
import { PageEdit, type PageElementData } from '@ai-content-hub/content-elements';
import type { BlockContentType } from '@ai-content-hub/content-elements';
import type { EssayData, EssayConfig } from './schema';
import { defaultEssayData, GRADING_QUESTION_TYPES } from './schema';
import { FileText, HelpCircle, Settings } from 'lucide-react';

const ALLOWED_BLOCK_TYPES: BlockContentType[] = [
    'rich-text',
    'image',
    'audio',
    'office-document',
];

export interface EssayEditProps {
    value: EssayData;
    onChange: (value: EssayData) => void;
    config?: EssayConfig;
    onConfigChange?: (config: EssayConfig) => void;
    disabled?: boolean;
    /** Slot for Configuration panel (from ContentEditor when hasIntegratedConfigTab) */
    configSlot?: React.ReactNode;
    /** Validation errors keyed by field path */
    errors?: Record<string, string>;
    className?: string;
}

export const EssayEdit: React.FC<EssayEditProps> = ({
    value: data,
    onChange,
    config: _config,
    onConfigChange: _onConfigChange,
    disabled = false,
    configSlot,
    errors,
    className,
}) => {
    const { t } = useLocale({ config: _config?.i18n });
    const [activeTab, setActiveTab] = React.useState<'content' | 'questions' | 'config'>('content');

    const safeData: EssayData = {
        ...defaultEssayData,
        ...data,
        lessonContent: data?.lessonContent ?? defaultEssayData.lessonContent,
        essayQuestion: data?.essayQuestion ?? defaultEssayData.essayQuestion,
    };

    const updateData = (updates: Partial<EssayData>) => {
        onChange({ ...safeData, ...updates });
    };

    const updateQuestion = (updates: Partial<EssayData['essayQuestion']>) => {
        updateData({
            essayQuestion: { ...safeData.essayQuestion, ...updates },
        });
    };

    const handleLessonChange = (lessonContent: PageElementData) => {
        updateData({ lessonContent });
    };

    const contentSectionCount = safeData.lessonContent.sections?.length ?? 0;
    const hasQuestion = (() => {
        const q = safeData.essayQuestion.question;
        if (q && typeof q === 'object' && isTiptapDocument(q)) {
            // Non-empty TiptapJSON has at least one node with text content
            return Boolean(q.content && q.content.length > 0);
        }
        return typeof q === 'string' && Boolean(q.trim());
    })();

    const essayTabs: EditTabDescriptor[] = React.useMemo(
        () => [
            { id: 'content', label: t('contentTab'), icon: FileText, badge: contentSectionCount },
            { id: 'questions', label: t('questionsTab'), icon: HelpCircle, badge: hasQuestion ? 1 : 0 },
            ...(configSlot ? [{ id: 'config', label: t('configuration'), icon: Settings }] : []),
        ],
        [t, contentSectionCount, hasQuestion, configSlot]
    );

    return (
        <CompositeEditShell
            prefix="essay-edit"
            tabs={essayTabs}
            activeTab={activeTab}
            onTabChange={(id) => setActiveTab(id as 'content' | 'questions' | 'config')}
            configSlot={configSlot}
            className={cn('essay-edit h-full min-h-0', className)}
            renderPanel={(tabId) => {
                if (tabId === 'content') {
                    return (
                        <CompositeEditContentTab
                            prefix="essay-edit"
                            disabled={disabled}
                            title={{
                                id: 'essay-title',
                                label: t('title'),
                                value: safeData.title || '',
                                onChange: (v) => updateData({ title: v }),
                                placeholder: t('essayEnterTitle'),
                                required: true,
                            }}
                            titleInputClassName="essay-edit-title-input"
                            description={{
                                id: 'essay-description',
                                label: t('essayInstructionsOptional'),
                                value: safeData.description || '',
                                onChange: (v) => updateData({ description: v }),
                                placeholder: t('essayEnterInstructions'),
                            }}
                            descriptionAsInput
                            descriptionInputClassName="essay-edit-instructions-input"
                            page={
                                <PageEdit
                                    value={safeData.lessonContent}
                                    onChange={handleLessonChange}
                                    disabled={disabled}
                                    hideSettings
                                    allowedBlockTypes={ALLOWED_BLOCK_TYPES}
                                    className="essay-edit-page-inner"
                                    config={_config}
                                />
                            }
                        />
                    );
                }
                if (tabId === 'questions') {
                    return (
                        <div className="essay-edit-questions essay-edit-questions-panel flex-1 min-h-0 overflow-y-auto px-1">
                            <EssayQuestionEditor
                                question={safeData.essayQuestion}
                                onChange={updateQuestion}
                                disabled={disabled}
                                errors={errors}
                                config={_config}
                            />
                        </div>
                    );
                }
                return null;
            }}
        />
    );
};

/**
 * Convert a string | TiptapJSON question to a TiptapJSON value for the editor.
 */
function questionToTiptap(question: string | TiptapJSON | undefined): TiptapJSON {
    if (question && typeof question === 'object' && isTiptapDocument(question)) {
        return question;
    }
    if (typeof question === 'string' && question.trim()) {
        return createParagraphContent(question);
    }
    return createEmptyDocument();
}

/**
 * Question tab editor
 */
const EssayQuestionEditor: React.FC<{
    question: EssayData['essayQuestion'];
    onChange: (updates: Partial<EssayData['essayQuestion']>) => void;
    disabled: boolean;
    errors?: Record<string, string>;
    config?: EssayConfig;
}> = ({ question, onChange, disabled, errors, config }) => {
    const { t } = useLocale({ config: config?.i18n });
    return (
        <div className="essay-question-editor essay-edit-questions-form space-y-6 rounded-xl border-0 bg-card p-4 shadow-sm">
            <div className="space-y-2">
                <Label htmlFor="essay-question">
                    {t('question')} <span className="text-destructive">*</span>
                </Label>
                <RichTextEditor
                    id="essay-question"
                    value={questionToTiptap(question.question)}
                    onChange={(doc: TiptapJSON) => onChange({ question: doc })}
                    placeholder={t('essayQuestionPlaceholder')}
                    readOnly={disabled}
                    minHeight={120}
                    className={cn('essay-edit-questions-field', errors?.['essayQuestion.question'] && 'border-destructive')}
                    error={Boolean(errors?.['essayQuestion.question'])}
                />
                {errors?.['essayQuestion.question'] && (
                    <p className="essay-edit-error text-sm text-destructive">{errors['essayQuestion.question']}</p>
                )}
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label htmlFor="essay-min-words">{t('essayMinWords')}</Label>
                    <Input
                        id="essay-min-words"
                        type="number"
                        value={question.minWords ?? 250}
                        onChange={(e) => onChange({ minWords: parseInt(e.target.value) || 0 })}
                        disabled={disabled}
                        min={0}
                        className="essay-edit-questions-field"
                    />
                </div>
                <div className="space-y-1.5">
                    <Label htmlFor="essay-max-words">{t('essayMaxWords')}</Label>
                    <Input
                        id="essay-max-words"
                        type="number"
                        value={question.maxWords ?? 500}
                        onChange={(e) => onChange({ maxWords: parseInt(e.target.value) || 0 })}
                        disabled={disabled}
                        min={0}
                        className={cn('essay-edit-questions-field', errors?.['essayQuestion.maxWords'] && 'border-destructive')}
                    />
                    {errors?.['essayQuestion.maxWords'] && (
                        <p className="essay-edit-error text-sm text-destructive">{errors['essayQuestion.maxWords']}</p>
                    )}
                </div>
            </div>

            <div className="flex items-center gap-3 py-2">
                <Checkbox
                    id="essay-auto-grading"
                    checked={question.autoGrading ?? false}
                    onCheckedChange={(checked) =>
                        onChange({
                            autoGrading: checked === true,
                            gradingQuestionType: checked
                                ? (question.gradingQuestionType ?? 'QT_WRITE_ESSAY')
                                : undefined,
                        })
                    }
                    disabled={disabled}
                />
                <Label htmlFor="essay-auto-grading" className="cursor-pointer">
                    {t('essayAutoGradingLabel')}
                </Label>
            </div>

            {question.autoGrading && (
                <div className="space-y-2">
                    <Label htmlFor="essay-grading-type">
                        {t('essayGradingType')} <span className="text-destructive">*</span>
                    </Label>
                    <Select
                        value={question.gradingQuestionType ?? 'QT_WRITE_ESSAY'}
                        onValueChange={(v) => onChange({ gradingQuestionType: v })}
                        disabled={disabled}
                    >
                        <SelectTrigger id="essay-grading-type" className="essay-edit-questions-field">
                            <SelectValue placeholder={t('essaySelectGradingType')} />
                        </SelectTrigger>
                        <SelectContent>
                            {GRADING_QUESTION_TYPES.map((gt) => (
                                <SelectItem key={gt.code} value={gt.code}>
                                    {gt.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            )}
        </div>
    );
};

export default EssayEdit;
